import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import { FiFileText, FiFolder, FiGrid, FiSearch } from 'react-icons/fi';
import { departmentAPI, fileAPI, projectAPI } from '../utils/api';
import './DashboardPage.css';

const normalizeFilesData = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.files)) return data.files;
  return [];
};

const AnalyticsDataExplorerPage = () => {
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [departmentFilter, setDepartmentFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');

        const [deptsResponse, projectsResponse, filesResponse] = await Promise.all([
          departmentAPI.getAllDepartments(),
          projectAPI.getAllProjects(),
          fileAPI.getAllFiles()
        ]);

        const loadedDepartments = Array.isArray(deptsResponse?.data) ? deptsResponse.data : [];
        setDepartments(loadedDepartments);
        setProjects(Array.isArray(projectsResponse?.data) ? projectsResponse.data : []);
        setFiles(normalizeFilesData(filesResponse?.data));

        const savedDeptRaw = localStorage.getItem('selectedDept');
        const savedDept = savedDeptRaw ? JSON.parse(savedDeptRaw) : null;
        if (savedDept?.id && loadedDepartments.some((dept) => Number(dept.id) === Number(savedDept.id))) {
          setDepartmentFilter(String(savedDept.id));
        }
      } catch (err) {
        setError('Failed to load data explorer.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const projectMap = useMemo(() => {
    const map = new Map();
    projects.forEach((project) => {
      map.set(Number(project.id), project);
    });
    return map;
  }, [projects]);

  const departmentMap = useMemo(() => {
    const map = new Map();
    departments.forEach((department) => {
      map.set(Number(department.id), department);
    });
    return map;
  }, [departments]);

  const projectOptions = useMemo(() => {
    if (!departmentFilter) return projects;
    return projects.filter(
      (project) => Number(project.departmentId) === Number(departmentFilter)
    );
  }, [projects, departmentFilter]);

  const filteredRows = useMemo(() => {
    let rows = files.map((file) => {
      const project = projectMap.get(Number(file.projectId));
      const department = project ? departmentMap.get(Number(project.departmentId)) : null;

      return {
        id: file.id,
        fileName: file.fileName || '-',
        fileType: file.fileType || '-',
        description: file.description || '-',
        projectName: project?.name || '-',
        departmentName: department?.name || '-',
        uploadedAt: formatDateTime(file.createdAt || file.updatedAt)
      };
    });

    if (departmentFilter) {
      rows = rows.filter((row) => {
        const dept = departments.find((d) => d.name === row.departmentName);
        return Number(dept?.id) === Number(departmentFilter);
      });
    }

    if (projectFilter) {
      const selectedProject = projects.find((project) => Number(project.id) === Number(projectFilter));
      rows = rows.filter((row) => row.projectName === (selectedProject?.name || ''));
    }

    if (search.trim()) {
      const term = search.trim().toLowerCase();
      rows = rows.filter((row) => {
        return (
          row.fileName.toLowerCase().includes(term) ||
          row.projectName.toLowerCase().includes(term) ||
          row.departmentName.toLowerCase().includes(term) ||
          row.fileType.toLowerCase().includes(term) ||
          row.description.toLowerCase().includes(term)
        );
      });
    }

    return rows;
  }, [files, projectMap, departmentMap, departmentFilter, projectFilter, search, departments, projects]);

  const distinctDepartmentCount = useMemo(
    () => new Set(filteredRows.map((r) => r.departmentName).filter((v) => v && v !== '-')).size,
    [filteredRows]
  );
  const distinctProjectCount = useMemo(
    () => new Set(filteredRows.map((r) => r.projectName).filter((v) => v && v !== '-')).size,
    [filteredRows]
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 mb-0">Loading data explorer...</p>
      </div>
    );
  }

  return (
    <Container fluid className="dashboard-page py-4">
      <div className="mb-4">
        <h1 className="page-title">Data Explorer</h1>
        <p className="page-subtitle mb-0">Search and filter every uploaded file across departments and projects.</p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Row className="g-3 mb-3">
        <Col md={4}>
          <Card className="stat-card stat-primary h-100">
            <Card.Body className="d-flex align-items-center gap-3">
              <div className="stat-icon"><FiFileText /></div>
              <div>
                <p className="stat-title mb-1">Files Matching Filters</p>
                <h3 className="stat-value mb-0">{filteredRows.length}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="stat-card stat-info h-100">
            <Card.Body className="d-flex align-items-center gap-3">
              <div className="stat-icon"><FiGrid /></div>
              <div>
                <p className="stat-title mb-1">Departments Represented</p>
                <h3 className="stat-value mb-0">{distinctDepartmentCount}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="stat-card stat-success h-100">
            <Card.Body className="d-flex align-items-center gap-3">
              <div className="stat-icon"><FiFolder /></div>
              <div>
                <p className="stat-title mb-1">Projects Represented</p>
                <h3 className="stat-value mb-0">{distinctProjectCount}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm mb-3 card-accent-top">
        <Card.Body>
          <Row className="g-3">
            <Col md={3}>
              <Form.Label className="mb-1">Department</Form.Label>
              <Form.Select
                value={departmentFilter}
                onChange={(e) => {
                  setDepartmentFilter(e.target.value);
                  setProjectFilter('');
                }}
              >
                <option value="">All Departments</option>
                {departments.map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={3}>
              <Form.Label className="mb-1">Project</Form.Label>
              <Form.Select
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
              >
                <option value="">All Projects</option>
                {projectOptions.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={6}>
              <Form.Label className="mb-1">Search</Form.Label>
              <div className="position-relative">
                <FiSearch className="position-absolute text-muted" style={{ top: '50%', left: 12, transform: 'translateY(-50%)' }} />
                <Form.Control
                  type="text"
                  placeholder="Search file, project, department, type..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ paddingLeft: 36 }}
                />
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm card-accent-top">
        <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Results</h5>
          <small className="text-muted">Rows: {filteredRows.length}</small>
        </Card.Header>
        <Card.Body>
          {filteredRows.length === 0 ? (
            <div className="empty-state">
              <div className="icon-chip icon-chip-lg icon-chip-secondary">
                <FiSearch />
              </div>
              <div className="empty-state-title">No records match current filters</div>
              <p className="empty-state-subtitle mb-0">Try clearing the department, project, or search filters above.</p>
            </div>
          ) : (
            <div className="table-responsive dashboard-table-wrap">
              <Table striped bordered hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>File Name</th>
                    <th>Type</th>
                    <th>Department</th>
                    <th>Project</th>
                    <th>Description</th>
                    <th>Uploaded</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row, index) => (
                    <tr key={row.id || index}>
                      <td>{index + 1}</td>
                      <td>{row.fileName}</td>
                      <td>{row.fileType}</td>
                      <td>{row.departmentName}</td>
                      <td>{row.projectName}</td>
                      <td>{row.description}</td>
                      <td>{row.uploadedAt}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default AnalyticsDataExplorerPage;
